import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { DataGrid, ptBR } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AppDialog from "~/shared/ui/AppDialog";
import CustomPagination from "~/components/CustomPagination/index";
import { formatDateInHoursAndMinutes } from "~/utils/date";
import { formatIsoDateBR } from "~/utils/format";
import { numberToBRL } from "~/utils/number";
import { deadlineLabel, RegistrationSealChip } from "~/features/registration-seal";
import { updateNormalPaymentDate } from "~/services/antecipebackoffice/advanceRequest";
import { getApiErrorMessage } from "~/utils/apiError";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import NormalPaymentDateCell from "./NormalPaymentDateCell";

const LIMIT_DUTYS = 10;
// Depois do envio da empresa só o backoffice alcança o plantão; antes disso a data é da conferência da empresa.
const NORMAL_DATE_EDITABLE_STATUSES = ["waiting_generate_doctor_contract", "waiting_doctor_sign_contract"];
const percent = new Intl.NumberFormat("pt-BR", { style: "percent", maximumFractionDigits: 1 });
const hour = (value) => (value ? formatDateInHoursAndMinutes(new Date(value)) : "Não informado");
const Flag = ({ value }) => (value ? <CheckIcon color="primary" aria-label="Sim" /> : <CloseIcon color="error" aria-label="Não" />);

// Detalhe do selo: motivo dado pelo ME + prazo de envio ("vence em 2 d", "vencido há 3 d", "prazo padrão").
const registrationDetail = (reg) => [reg?.reasonLabel, deadlineLabel(reg)].filter(Boolean).join(" · ") || "—";

// showRegistration: chave registrationSeal (conversão do ME) — selo e prazo por plantão.
// canWrite: operador do backoffice pode preencher a data de pagamento normal (aguardando contrato/assinatura).
const DutysModal = ({ open, setOpen, handleClose, dutys, updateListDutys, totalRegisters, updateRequestList, advancePayment, showRegistration = false, canWrite = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const notify = useNotify();
  const normalDateEditable = Boolean(canWrite && advancePayment && NORMAL_DATE_EDITABLE_STATUSES.includes(advancePayment.status));

  const reloadPage = async (pageParam) => {
    if (typeof updateListDutys !== "function") return;
    setIsLoading(true);
    try {
      await updateListDutys(false, { limit: LIMIT_DUTYS, offset: ((pageParam || page) - 1) * LIMIT_DUTYS });
    } catch (e) {
      // Igual ao comportamento anterior: falha ao recarregar a página não derruba o modal.
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const saveNormalPaymentDate = async (vacancyAdvanceId, date) => {
    try {
      await updateNormalPaymentDate(advancePayment.id, vacancyAdvanceId, date);
      notify({ severity: "success", message: "Data de pagamento normal atualizada com sucesso!" });
      await reloadPage();
    } catch (e) {
      notify({ severity: "error", message: getApiErrorMessage(e, "Erro ao atualizar data de pagamento normal!") });
    }
  };

  const onClose = async (e) => {
    if (typeof handleClose === "function") handleClose(e);
    if (typeof setOpen === "function") {
      setOpen(false);
      setPage(1);
      if (typeof updateRequestList === "function") await updateRequestList();
    }
  };

  const col = (field, headerName, width, extra = {}) => ({ field, headerName, width, editable: false, sortable: false, ...extra });
  // Só leitura, exceto a data de pagamento normal (normalDateEditable): sem ação de sincronizar plantão.
  const columns = [
    col("id", "ID", 90),
    col("hospital_name", "Hospital", 200),
    col("date", "Data", 110, { valueFormatter: ({ value }) => formatIsoDateBR(value) || "—" }),
    ...(showRegistration
      ? [
          col("registration", "Registro no ME", 170, { renderCell: ({ row }) => <RegistrationSealChip registration={row.registration} /> }),
          // Quebra linha (a linha cresce): o prazo não some cortado e não há rolagem horizontal nova (CONVB-21).
          col("registration_detail", "Detalhe do registro", 260, {
            renderCell: ({ row }) => (
              <Box component="span" data-testid="registration-detail" sx={{ whiteSpace: "normal", lineHeight: 1.35, py: 0.75, fontSize: 13 }}>
                {registrationDetail(row.registration)}
              </Box>
            ),
          }),
        ]
      : []),
    col("normal_payment_date", "Data de pagamento normal", normalDateEditable ? 220 : 190, {
      renderCell: ({ row }) => <NormalPaymentDateCell row={row} editable={normalDateEditable} onSave={saveNormalPaymentDate} />,
    }),
    col("gross_value", "Valor bruto", 120, { type: "number", valueFormatter: ({ value }) => numberToBRL(value) }),
    col("real_net_value", "Valor Proporcional", 150, { type: "number", valueFormatter: ({ value }) => numberToBRL(value) }),
    col("percentage_time", "Porcentagem Trabalhada", 170, { type: "number", valueFormatter: ({ value }) => (value == null ? "—" : percent.format(Number(value))) }),
    col("start_hour", "Hora de inicio", 120, { valueFormatter: ({ value }) => hour(value) }),
    col("end_hour", "Hora da saida", 120, { valueFormatter: ({ value }) => hour(value) }),
    col("real_start_hour", "Hora de inicio realizada", 170, { valueFormatter: ({ value }) => hour(value) }),
    col("real_end_hour", "Hora da saida realizada", 170, { valueFormatter: ({ value }) => hour(value) }),
    col("with_discount", "Com desconto?", 130, { align: "center", headerAlign: "center", renderCell: ({ value }) => <Flag value={value} /> }),
    col("verified_documents", "Documentos Verificados?", 190, { align: "center", headerAlign: "center", renderCell: ({ value }) => <Flag value={value} /> }),
    col("can_advance", "Pode Adiantar?", 130, { align: "center", headerAlign: "center", renderCell: ({ value }) => <Flag value={value} /> }),
    col("is_checked", "Já Revisado?", 130, { align: "center", headerAlign: "center", renderCell: ({ value }) => <Flag value={value} /> }),
  ];

  const title = advancePayment ? `Plantões do pacote #${advancePayment.id}` : "Validar plantões do médico";
  const context = advancePayment ? [advancePayment.doctor_name, advancePayment.company_name].filter(Boolean).join(" · ") : undefined;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="lg"
      title={title}
      context={context}
      footerLeft={<Typography variant="caption" color="text.secondary">{totalRegisters || 0} plantões · % trabalhada arredondada</Typography>}
      actions={<Button variant="contained" onClick={onClose}>Fechar</Button>}
    >
      <Box sx={{ width: "100%" }}>
        <DataGrid
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          rows={dutys}
          columns={columns}
          autoHeight
          getRowHeight={showRegistration ? () => "auto" : undefined}
          disableVirtualization
          disableColumnMenu
          disableColumnFilter
          disableSelectionOnClick
          loading={isLoading}
          components={{
            Pagination: () => (
              <CustomPagination
                count={Math.ceil((totalRegisters || 0) / LIMIT_DUTYS)}
                page={page}
                setPage={setPage}
                onChange={(event, value) => reloadPage(value)}
              />
            ),
          }}
        />
      </Box>
    </AppDialog>
  );
};

export default DutysModal;
